<template>
  <div class="portfolio-container">
    <header>
      <div class="profile-section">
        <div v-if="portfolio.profileImage" class="profile-image-container">
          <picture>
            <!-- WebP format with srcset -->
            <source 
              type="image/webp"
              :srcset="generateSrcSet(portfolio.profileImage, 'webp')"
              sizes="(max-width: 768px) 140px, 180px"
            />
            <!-- Original format with srcset -->
            <img 
              :src="portfolio.profileImage"
              :srcset="generateSrcSet(portfolio.profileImage)"
              sizes="(max-width: 768px) 140px, 180px"
              :alt="portfolio.name"
              class="profile-photo"
              loading="lazy"
            />
          </picture>
        </div>
        <div>
          <h1>{{ portfolio.name }}</h1>
          <div class="tagline">{{ portfolio.tagline }}</div>
        </div>
      </div>
      
      <div class="social-links">
        <a 
          v-for="(link, index) in portfolio.social" 
          :key="index" 
          :href="link.url" 
          target="_blank" 
          :title="link.name"
        >
          <font-awesome-icon :icon="link.icon" />
        </a>
      </div>
    </header>
    
    <section class="about">
      <h2>About Me</h2>
      <p>{{ portfolio.about }}</p>
      <a :href="portfolio.resume" download class="download-button">
        <font-awesome-icon icon="file-download" /> Download CV
      </a>
    </section>
    
    <section class="projects">
      <h2>Projects</h2>
      <div class="project-cards">
        <div 
          v-for="(project, index) in portfolio.projects" 
          :key="index" 
          class="project-card"
        >
          <h3>{{ project.name }}</h3>
          <p>{{ project.description }}</p>
          <a :href="project.url" class="project-link">
            Learn more
            <span class="visually-hidden">about {{ project.name }}</span>
          </a>
        </div>
      </div>
    </section>
    
    <section class="skills">
      <h2>Skills</h2>
      <div class="skill-categories">
        <div 
          v-for="(category, index) in portfolio.skills" 
          :key="index" 
          class="skill-category"
        >
          <h3>{{ category.category }}</h3>
          <ul class="skill-list">
            <li v-for="(skill, skillIndex) in category.items" :key="skillIndex">
              {{ skill }}
            </li>
          </ul>
        </div>
      </div>
    </section>
    
    <footer>
      <p>&copy; {{ new Date().getFullYear() }} {{ portfolio.name }}. All rights reserved.</p>
    </footer>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import yaml from 'js-yaml';

export default {
  name: 'PortfolioSection',
  setup() {
    const portfolio = ref({
      name: '',
      tagline: '',
      social: [],
      about: '',
      resume: '',
      projects: [],
      skills: []
    });

    // Generate srcset based on the image path
    const generateSrcSet = (imagePath, format = null) => {
      if (!imagePath) return '';
      
      // Extract base path and original extension
      const lastDot = imagePath.lastIndexOf('.');
      const basePath = imagePath.substring(0, lastDot);
      let extension = imagePath.substring(lastDot + 1);
      
      // Use specified format or original extension
      if (format) {
        extension = format;
      }
      
      // Define sizes for responsive images
      const sizes = [320, 640, 960, 1280];
      
      // Generate the srcset string
      return sizes.map(size => 
        `${basePath}-${size}.${extension} ${size}w`
      ).join(', ');
    };

    const loadPortfolioData = async () => {
      try {
        const response = await fetch('/config/portfolio.yaml');
        const yamlText = await response.text();
        const data = yaml.load(yamlText);
        portfolio.value = data;
      } catch (error) {
        console.error('Failed to load portfolio data:', error);
      }
    };

    onMounted(() => {
      loadPortfolioData();
    });

    return {
      portfolio,
      generateSrcSet
    };
  }
};
</script>

<style scoped>
.portfolio-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  color: #ffffff;
  font-family: 'Roboto', sans-serif;
  position: relative;
  z-index: 1;
}

.profile-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
}

.profile-photo {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(143, 143, 255, 0.5);
  box-shadow: 0 0 20px rgba(120, 120, 255, 0.3);
}

header {
  text-align: center;
  padding: 80px 0 40px;
}

h1 {
  font-size: 3.5rem;
  margin: 0;
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 0 0 10px rgba(120, 120, 255, 0.5);
}

.tagline {
  font-size: 1.5rem;
  opacity: 0.8;
  margin: 10px 0 20px;
}

.social-links {
  margin: 20px 0;
}

.social-links a {
  display: inline-block;
  margin: 0 15px;
  color: #ffffff;
  font-size: 1.8rem;
  transition: color 0.3s ease, transform 0.3s ease;
}

.social-links a:hover {
  color: #8f8fff;
  transform: translateY(-3px);
}

section {
  margin: 100px 0;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  padding: 30px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 20, 0.3);
  border: 1px solid rgba(120, 120, 255, 0.1);
}

h2 {
  margin-top: 0;
  color: #8f8fff;
  font-size: 2rem;
  margin-bottom: 20px;
  position: relative;
  display: inline-block;
}

h2::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -5px;
  height: 3px;
  width: 40px;
  background: linear-gradient(90deg, #8f8fff, transparent);
}

.project-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.project-card {
  background-color: rgba(20, 20, 40, 0.6);
  padding: 25px;
  border-radius: 8px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.project-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 7px 25px rgba(120, 120, 255, 0.2);
}

.project-card h3 {
  color: #ffffff;
  margin-top: 0;
}

.project-link {
  display: inline-block;
  margin-top: 15px;
  color: #8f8fff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.project-link:hover {
  color: #ffffff;
}

.skill-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.skill-category h3 {
  color: #8f8fff;
  margin-top: 0;
  font-size: 1.3rem;
}

.skill-list {
  list-style-type: none;
  padding: 0;
}

.skill-list li {
  padding: 8px 0;
  position: relative;
  padding-left: 20px;
}

.skill-list li::before {
  content: '•';
  color: #8f8fff;
  position: absolute;
  left: 0;
}


.about .download-button {
  display: inline-block;
  background: linear-gradient(135deg, #8f8fff, #536dfe);
  color: white;
  padding: 12px 25px;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 500;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin-top: 20px;
  box-shadow: 0 4px 15px rgba(120, 120, 255, 0.3);
}

.about .download-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 7px 20px rgba(120, 120, 255, 0.4);
}

footer {
  text-align: center;
  margin-top: 100px;
  padding: 20px;
  opacity: 0.7;
  font-size: 0.9rem;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

@media (max-width: 768px) {
  .portfolio-container {
    padding: 20px 15px;
  }
  
  header {
    padding: 40px 0 20px;
  }
  
  h1 {
    font-size: 2.5rem;
  }
  
  .tagline {
    font-size: 1.2rem;
  }
  
  section {
    margin: 50px 0;
    padding: 20px;
  }
  
  .skill-categories, .project-cards {
    grid-template-columns: 1fr;
  }
  
  .profile-photo {
    width: 140px;
    height: 140px;
  }
}
</style>
